'use client';

import React, { useRef, useEffect } from 'react';
import { Text, Box, SimpleGrid, Stack } from '@chakra-ui/react';
import { IShipper } from '@/_types';
import { useTranslations, useLocale } from 'next-intl';
import { useMemo } from 'react';
import { buildSections } from '@/features/brand-menu';
import type { ICategoryWithProducts } from '@/features/brand-menu';
import RestaurantHeader from '../../restarant-header';
import Breadcrumbs from '@/_components/Breadcrumbs';
import BreadcrumbJsonLd from '@/_components/Breadcrumbs/BreadcrumbJsonLd';
import { buildCatalogBreadcrumbs } from '@/_components/Breadcrumbs/buildCatalogBreadcrumbs';
import { getBrandHref } from '@/_lib/brand-href';
import Card from '@/_components/Card';
import CategoriesSection from '../components/categories-section';
import Bottom from './bottom';
import AlertWorkingHours from '@/_components/AlertWorkingHours';
import { Alert } from '@/shared/ui/alert';
import { useDispatch, useSelector } from 'react-redux';
import { IRedux } from '@/_store/types';
import { sessionActions } from '@/_store/session/session.slice';
import { onOpenMap } from '@/_store/common/common.slice';
import { Button } from '@/shared/ui/button';

interface BrandSingleMobileProps {
  brand: IShipper;
  categories?: ICategoryWithProducts[];
  zoneError: boolean;
}

const BrandSingleMobile: React.FC<BrandSingleMobileProps> = ({
  brand,
  categories = [],
  zoneError,
}) => {
  const t = useTranslations();
  const locale = useLocale();
  const dispatch = useDispatch();
  const { data } = useSelector((state: IRedux) => state.settings);
  const { isMapModal } = useSelector((state: IRedux) => state.common);

  const sections = useMemo(
    () => buildSections(categories, locale),
    [categories, locale],
  );

  const catalogRef = useRef<HTMLDivElement | null>(null);

  const handleTakeawayClick = () => {
    const activeOrderType = data?.data?.order_types?.find(
      (orderType) => orderType.type === 'self-pickup',
    );
    if (activeOrderType) {
      dispatch(sessionActions.saveOrderType(activeOrderType));
      if (!isMapModal) {
        dispatch(onOpenMap());
      }
    }
  };

  const breadcrumbData = useMemo(
    () =>
      buildCatalogBreadcrumbs({
        homeLabel: t('home'),
        brand: { name: brand.name, id: brand.id },
      }),
    [t, brand.name, brand.id],
  );

  // Scroll to category when entering page with hash (e.g. /brand/yaponamama#category-slug)
  useEffect(() => {
    if (!sections.length) return;
    const hash =
      typeof window !== 'undefined' ? window.location.hash.slice(1) : '';
    if (!hash) return;
    const el = document.getElementById(hash);
    if (el) {
      const headerOffset = 80;
      const elementPosition = el.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - headerOffset;
      const timeoutId = window.setTimeout(() => {
        window.scrollTo({
          top: Math.max(0, offsetPosition),
          behavior: 'smooth',
        });
      }, 100);
      return () => window.clearTimeout(timeoutId);
    }
  }, [sections]);

  return (
    <Box bg='#fff' minH={'100dvh'} pb='70px'>
      <RestaurantHeader brand={brand} />
      <Box px={4}>
        <Breadcrumbs data={breadcrumbData} lastAsH1 />
      </Box>
      <BreadcrumbJsonLd
        data={breadcrumbData}
        currentHref={getBrandHref(brand.id)}
      />
      {zoneError && (
        <Box px={4} mt={5}>
          <Alert
            status='error'
            title={t('address_not_in_service_area')}
            rounded='2xl'
          >
            <Button mt={3} onClick={handleTakeawayClick}>
              {t('order_for_pickup')}
            </Button>
          </Alert>
        </Box>
      )}
      <Box px={4} mt={{ base: 6, lg: 0 }}>
        <AlertWorkingHours />
      </Box>
      <CategoriesSection catalogRef={catalogRef} categories={categories} />
      <Stack p={4} gap={4}>
        {sections.length === 0 ? (
          <Box py={10} textAlign='center' color='text.muted'>
            {t('no_products')}
          </Box>
        ) : (
          sections.map((section) => (
            <Stack key={section.id} id={section.slug} gap={3}>
              <Text fontSize='xl' fontWeight={600}>
                {section.title}
              </Text>
              <SimpleGrid columns={{ base: 2, sm: 2 }} gap={{ base: 2, lg: 3 }}>
                {section.products.map((product) => (
                  <Card
                    product={product}
                    discounts={[]}
                    category={null}
                    key={`${section.id}-${product.id}-${product.image}`}
                  />
                ))}
              </SimpleGrid>
            </Stack>
          ))
        )}
      </Stack>
      {!zoneError && <Bottom />}
    </Box>
  );
};

export default BrandSingleMobile;
